import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Flag from 'react-country-flag'; // npm install react-country-flag
 
const API_URL = "https://api.securxperts.com:8000";
const ENDPOINT = "/forms/contact";
 
// ====================== COUNTRIES DATA ======================
const COUNTRIES = [
  { code: "+91", name: "India", iso: "IN" },
  { code: "+1", name: "United States", iso: "US" },
  { code: "+44", name: "United Kingdom", iso: "GB" },
  { code: "+61", name: "Australia", iso: "AU" },
  { code: "+971", name: "United Arab Emirates", iso: "AE" },
  // Add more countries if needed
];
 
// ====================== COUNTRY DROPDOWN ======================
const CountryDropdown = ({ value, onChange, error, touched }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
 
  const filteredCountries = COUNTRIES.filter(
    (c) =>
      c.code.includes(search) ||
      c.name.toLowerCase().includes(search.toLowerCase())
  );
 
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
 
  const selected = COUNTRIES.find((c) => c.code === value) || null;
 
  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`w-full p-3 rounded-lg border cursor-pointer flex items-center gap-2 transition-all ${
          error && touched
            ? "border-orange-400 bg-orange-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected ? (
          <>
            <Flag countryCode={selected.iso} svg style={{ width: '24px', height: '16px', borderRadius: '2px' }} />
            <span className="font-medium">{selected.code}</span>
          </>
        ) : (
          <span className="text-gray-500">Country Code</span>
        )}
        <span className="ml-auto text-gray-400">▼</span>
      </div>
 
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search country or code..."
            className="w-full p-2 border-b focus:outline-none"
            autoFocus
          />
          {filteredCountries.map((country) => (
            <div
              key={country.code}
              className="p-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
              onClick={() => {
                onChange({ target: { name: "country_code", value: country.code } });
                setIsOpen(false);
                setSearch("");
              }}
            >
              <Flag countryCode={country.iso} svg style={{ width: '24px', height: '16px', borderRadius: '2px' }} />
              <span>{country.code}</span>
              <span className="text-gray-500 text-sm ml-2">{country.name}</span>
            </div>
          ))}
        </div>
      )}
 
      {error && touched && <p className="mt-1 text-sm text-orange-600">{error}</p>}
    </div>
  );
};
 
// ====================== SUCCESS POPUP ======================
const SuccessPopup = ({ isOpen, onClose }) => {
  const timeoutRef = useRef(null);
 
  useEffect(() => {
    if (isOpen) {
      timeoutRef.current = setTimeout(onClose, 5000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen, onClose]);
 
  if (!isOpen) return null;
 
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 max-w-sm w-full shadow-2xl relative animate-in fade-in zoom-in duration-200">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl"
        >
          ×
        </button>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold mb-2 text-green-600">Success!</h3>
          <p className="text-gray-600">Your request has been Submitted Successfully.</p>
        </div>
      </div>
    </div>
  );
};
 
/* ----------------------------------------------------
   IMPROVED CONTACT FORM
----------------------------------------------------- */
const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    country_code: "",
    phoneno: "",
    location: "",
    purpose: "",
    message: "",
  });
 
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
 
  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        return "";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email";
        return "";
      case "country_code":
        if (!value) return "Country code is required";
        return "";
      case "phoneno":
        if (!value.trim()) return "Phone number is required";
        if (value.length !== 10) return "Must be exactly 10 digits";
        return "";
      case "location":
      case "purpose":
      case "message":
        if (!value.trim()) return "This field is required";
        return "";
      default:
        return "";
    }
  };
 
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
 
    // Phone number cleaning
    if (name === "phoneno") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }
 
    // Name / location / purpose - only letters + spaces
    if (["name", "location", "purpose"].includes(name)) {
      newValue = value.replace(/[^a-zA-Z\s]/g, "");
    }
 
    setForm((prev) => ({ ...prev, [name]: newValue }));
    setTouched((prev) => ({ ...prev, [name]: true }));
 
    const error = validateField(name, newValue);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };
 
  useEffect(() => {
    const requiredFields = ["name", "email", "country_code", "phoneno", "location", "purpose", "message"];
    const allFilled = requiredFields.every((f) => form[f]?.trim());
    const noErrors = Object.values(errors).every((e) => !e);
    setIsValid(allFilled && noErrors);
  }, [form, errors]);
 
  const handleSubmit = async (e) => {
    e.preventDefault();
 
    if (!isValid) return;
 
    setSubmitting(true);
 
    try {
      const resp = await fetch(`${API_URL}${ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
 
      if (resp.ok) {
        setShowSuccess(true);
        // Reset form
        setForm({
          name: "", email: "", country_code: "", phoneno: "",
          location: "", purpose: "", message: "",
        });
        setErrors({});
        setTouched({});
      } else {
        alert("Submission failed. Please try again.");
      }
    } catch (err) {
      alert("Network error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };
 
  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="max-w-3xl mx-auto bg-white p-6 md:p-10 rounded-2xl shadow-lg border"
      >
        <h3 className="text-3xl font-bold text-center mb-8">
          Start Using ROB Today
        </h3>
 
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Name */}
          <div>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Full Name *"
              maxLength={60}
              required
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.name && touched.name ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.name && touched.name && (
              <p className="mt-1 text-sm text-orange-600">{errors.name}</p>
            )}
          </div>
 
          {/* Email */}
          <div>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              maxLength={60}
              placeholder="Email *"
              required
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.email && touched.email ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.email && touched.email && (
              <p className="mt-1 text-sm text-orange-600">{errors.email}</p>
            )}
          </div>
 
          {/* Country Code */}
          <div>
            <CountryDropdown
              value={form.country_code}
              onChange={handleChange}
              error={errors.country_code}
              touched={touched.country_code}
              required
            />
          </div>

          {/* Phone */}
          <div>
            <input
              name="phoneno"
              value={form.phoneno}
              onChange={handleChange}
              placeholder="Phone Number *"
              maxLength={10}
              required
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.phoneno && touched.phoneno ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.phoneno && touched.phoneno && (
              <p className="mt-1 text-sm text-orange-600">{errors.phoneno}</p>
            )}
          </div>

          {/* Location */}
          <div className="md:col-span-2">
            <input
              name="location"
              value={form.location}
              onChange={handleChange}
              maxLength={60}
              placeholder="Organisation Location *"
              required
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.location && touched.location ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.location && touched.location && (
              <p className="mt-1 text-sm text-orange-600">{errors.location}</p>
            )}
          </div>

          {/* Purpose */}
          <div className="md:col-span-2">
            <input
              name="purpose"
              value={form.purpose}
              maxLength={60}
              onChange={handleChange}
              placeholder="Purpose (Campus, Corporate, Training) *"
              required
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.purpose && touched.purpose ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.purpose && touched.purpose && (
              <p className="mt-1 text-sm text-orange-600">{errors.purpose}</p>
            )}
          </div>

          {/* Message */}
          <div className="md:col-span-2">
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us about your hiring process *"
              rows={4}
              required
              className={`w-full p-3 rounded-lg border outline-none transition-all resize-none ${
                errors.message && touched.message ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.message && touched.message && (
              <p className="mt-1 text-sm text-orange-600">{errors.message}</p>
            )}
          </div>
        </div>
 
        <button
          type="submit"
          disabled={submitting || !isValid}
          className={`w-full mt-8 py-3 rounded-full text-white font-medium transition-all ${
            isValid && !submitting
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {submitting ? "Submitting..." : "Start Using ROB Today"}
        </button>
      </form>
 
      {/* Success Popup */}
      <SuccessPopup
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
    </> 
  ); // Added closing brace here
};

/* ----------------------------------------------------
   MAIN PAGE (YOUR FULL UI – UNCHANGED)
----------------------------------------------------- */
function Rob() {
    const SCROLL_SHOW_THRESHOLD = 300;

    const [showScroll, setShowScroll] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);

    const scrollTimeoutRef = useRef(null);

    const RADIUS = 49;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
    const dashOffset =
      CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;

    const onScroll = useCallback(() => {
      const sc = window.scrollY || window.pageYOffset;
      setShowScroll(sc > SCROLL_SHOW_THRESHOLD);

      const doc = document.documentElement;
      const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
      const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;

      setScrollProgress(Math.min(100, Math.max(0, progress)));
      setIsScrolling(true);

      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        scrollTimeoutRef.current = null;
      }, 250);
    }, []);

    useEffect(() => {
      // Disable browser scroll restoration
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      
      // Force scroll to top on component mount with multiple attempts
      const forceScrollToTop = () => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        if (window.scrollY !== 0) {
          // If still not at top, try again after a short delay
          setTimeout(forceScrollToTop, 50);
        }
      };
      
      forceScrollToTop();
      
      // Additional fallback for stubborn browsers
      setTimeout(forceScrollToTop, 100);
      setTimeout(forceScrollToTop, 300);
      
      onScroll();

      window.addEventListener("scroll", onScroll, { passive: true });

      return () => {
        window.removeEventListener("scroll", onScroll);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        // Restore scroll restoration when component unmounts
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'auto';
        }
      };
    }, []);

    const scrollToTop = useCallback(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const handleKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        scrollToTop();
      }
    };

  return (
    <div className="w-full mt-10 font-sans text-[#0a1e42] overflow-hidden">
      <div className="fixed top-0 left-0 w-full z-[9999]">
        <Navbar />
      </div>

      {/* ----------------- HERO SECTION ----------------- */}
      <section className="w-full bg-gradient-to-br from-blue-50 to-white py-4 px-6 md:px-16">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center mt-[-80px]">
          {/* LEFT TEXT */}
          <div>
            <img src="/img/92.png" alt="Logo" className="w-32 mt-24 " />
            <h1 className="text-4xl font-bold leading-tight mt-8">
              Smart Recruitment & Onboarding Platform
            </h1>
            <p className="text-gray-600 mb-8">
              A smart platform that streamlines recruitment and onboarding for businesses, universities, colleges and training providers.
            </p>
          </div>

          {/* RIGHT IMAGE */}
          <div className="flex justify-center">
            <img
              src="/img/robimg.png"
              alt="Monitoring Dashboard"
              className="w-100 md:w-96 rounded-xl mt-32"
            />
          </div>
        </div>
      </section>

      {/* ----------------- VIDEO SECTION ----------------- */}
      <section className="w-full py-20 px-6 md:px-0 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto mb-10 text-center">
            <h2 className="text-3xl font-bold text-[#063852]">
              Recruitment & Onboarding Simplified
            </h2>
            <p className="text-gray-600 mt-3">
              Experience a smarter way to manage recruitment and onboarding for businesses and institutions. It reduces manual work, improves collaboration, and ensures a smooth experience for every new joiner.
            </p>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <video
              className="w-full h-auto max-h-[240px] sm:max-h-[360px] md:max-h-none block"
              src="/video/robngrab.mp4"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              controls
            >
              Your browser does not support HTML5 video.
            </video>
          </div>
        </div>
      </section>

      {/* ----------------- BEYOND THE RESUME ----------------- */}
      <section className="w-full  px-6 md:px-20">
        <h2 className="text-4xl font-extrabold text-center mb-4">
          Key Advantages of Using a Smart Recruitment Platform
        </h2>
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-4">
          Hire Smarter, Onboard Faster, and Engage Better Talent for a More Productive Workplace
        </p>

        <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
          <div className="text-center">
            <img src="/img/streamlinedhiring.png" className="w-16 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Streamlined Hiring Workflows</h3>
            <p className="text-gray-600 text-sm">
              Manage job postings, candidate shortlisting, interviews and assessments all in one central platform.
            </p>
          </div>

          <div className="text-center">
            <img src="/img/efficientonboarding.png" className="w-16 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Efficient Onboarding</h3>
            <p className="text-gray-600 text-sm">
              Automate onboarding tasks, checklists, and probation tracking to give new hires a smooth start.
            </p>
          </div>

          <div className="text-center">
            <img src="/img/improvedhr.png" className="w-16 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Improved HR Productivity</h3>
            <p className="text-gray-600 text-sm">
              Reduce manual work for HR teams and hiring managers, allowing them to focus on strategic tasks.
            </p>
          </div>
        </div>
      </section>

      {/* ----------------- PERSONALIZED SERVICES ----------------- */}
      <section className="w-full py-10 px-6 md:px-20 bg-white mb-8">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <img src="/img/report.png" className="rounded-2xl shadow-2xl w-full mb-[-100px]" />
          </div>

          <div className="mt-16">
            <h2 className="text-4xl font-extrabold mb-6">
              Innovative Smart Recruitment & Onboarding Platform
            </h2>

            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold mb-1">Automated Recruitment Workflows</h3>
                <p className="text-gray-600 text-sm">
                  Manage job postings, candidate shortlisting, interviews and assessments in a single intelligent platform, reducing manual effort and speeding up hiring.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-1">Expert Ads</h3>
                <p className="text-gray-600 text-sm">
                  Gain attention with professionally optimized job ads.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-1">Seamless Onboarding Experience</h3>
                <p className="text-gray-600 text-sm">
                  Automate onboarding tasks, checklists, document collection and probation tracking, giving new hires a smooth and structured start.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-1">Enhanced HR Productivity</h3>
                <p className="text-gray-600 text-sm">
                  Reduce repetitive administrative tasks for HR teams and hiring managers, allowing them to focus on strategic initiatives and employee engagement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----------------- KEY FEATURES SECTION ----------------- */}
      <section className="w-full py-24  px-6 md:px-20 bg-[#f7f9ff]">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-12">
          <h2 className="text-3xl font-bold mb-10">
            Key Features of ROB Recruitment & Onboarding Platform
          </h2>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="flex gap-4">
              <img src="/img/candidatescreening (1).png" className="w-12 h-12" />
              <div>
                <h3 className="font-semibold text-lg">Candidate Screening & Shortlisting</h3>
                <p className="text-gray-600 text-sm">
                  Efficiently evaluate applicants based on skills, experience and qualifications. Rank and shortlist top candidates for faster decision-making.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <img src="/img/rolenvacancy.png" className="w-12 h-12" />
              <div>
                <h3 className="font-semibold text-lg">Role & Vacancy Management</h3>
                <p className="text-gray-600 text-sm">
                  Create job roles, internship programmes or openings and share across multiple channels. Receive applications directly within ROB for centralised management.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <img src="/img/skillassessments.png" className="w-12 h-12" />
              <div>
                <h3 className="font-semibold text-lg">Skill Assessments & Testing</h3>
                <p className="text-gray-600 text-sm">
                  Conduct real-time assessments, coding challenges or skill-based tests. Auto-grading ensures objective results and fair candidate comparison.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <img src="/img/improvedhr.png" className="w-12 h-12" />
              <div>
                <h3 className="font-semibold text-lg">Interview Scheduling & Coordination</h3>
                <p className="text-gray-600 text-sm">
                  Automate interview scheduling with calendar integration. Notify candidates and interviewers via email or messaging for seamless coordination.
                </p>
              </div>
            </div>
          </div>

          {/* <div className="mt-14 bg-[#eef4ff] p-8 rounded-2xl border">
            <h3 className="font-bold text-xl mb-4">AI Resume Score</h3>
            <p className="text-gray-600 text-sm mb-6 max-w-md">
              Upload your resume and instantly receive actionable insights.
            </p>
            <img src="/img/resumeimg.png" className="w-full rounded-xl" />
          </div> */}
        </div>
      </section>

      {/* ----------------- CONTACT FORM ABOVE FOOTER ----------------- */}
      <section className="w-full py-5 px-6 md:px-20 bg-gray-100">
        <h2 className="text-4xl font-extrabold text-center mb-4">
          Talk to Our ROB Implementation Team
        </h2>
        <p className="text-center text-gray-600 max-w-xl mx-auto mb-10">
          Share your goals and we’ll help you implement ROB for your organisation.
        </p>

        <ContactForm />
      </section>

      <Footer />
      
      {/* Scroll to Top Button */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer
          flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r
          from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>
 
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
}
 
export default Rob;
 